/**
 * certificate.mjs — a credential that carries its own evidence.
 *
 * FOUNDER, 2026-08-15: "build p2 and p4".
 *
 * ── WHAT IS WRONG WITH AN ORDINARY CERTIFICATE ────────────────────────────────────────────────
 * It asserts. "This person completed X." That is a claim with no derivation — the fourth kind of
 * statement CLAUDECODE101 chapter 8 teaches learners to distrust, printed on stiff paper. Whoever
 * reads it can do nothing with it but believe the issuer.
 *
 * FP-018 gives the alternative. Every output is COMPUTE, QUOTE, or NULL. So a certificate here is
 * not an assertion that something was achieved; it is a RECORD of a check that ran:
 *
 *     what artefact · which check · what the check requires · when it passed · re-runnable
 *
 * A reader does not have to trust the issuer. They can read the standard, and they can run it.
 *
 * ── IT CERTIFIES ANY CHECKED ARTEFACT, NOT JUST A PROJECT ─────────────────────────────────────
 * Built general on purpose. P1's project and P2's product audit are both artefacts graded by a
 * published check, and a certificate mechanism that only understood one of them would have to be
 * rebuilt for the other. The kind is a field.
 *
 * ── THE LEDGER IS APPEND-ONLY AND A REVOCATION STAYS VISIBLE ──────────────────────────────────
 * A credential that can be quietly deleted is a credential nobody can rely on, and one that can be
 * quietly altered is worse. Issuing appends. Revoking appends. The revoked entry stays, with its
 * reason, because the honest record of a mistake includes the mistake.
 *
 * ── NOTHING IS ISSUED ON READING ──────────────────────────────────────────────────────────────
 * The format spec's law: no certificate without an artefact and a check. A learner who has read
 * every chapter and produced nothing has a record of reading, which is a different thing and is not
 * called a certificate here.
 */

export const SCHEMA = 'ankr-certificate-v1';

/**
 * The three things a certificate may say about a claim, and nothing else.
 * Straight from FP-018 — the same discipline the tutor applies to a dosage it cannot read.
 */
export const KINDS = Object.freeze(['project', 'audit']);

/**
 * Issue a certificate FROM A CHECK RESULT. Not from a request.
 *
 * The check result is the input, so there is no path through this function that produces a
 * certificate without one. `check.passes === true` is required — a near-miss is not a certificate
 * with a caveat, it is not a certificate.
 */
export function issue({ kind, principal, level, artefact, check, standardName, on }) {
  if (!KINDS.includes(kind)) return { ok: false, reason: `unknown certificate kind: ${kind}` };
  if (!principal) return { ok: false, reason: 'a certificate belongs to an identified principal — no anonymous holders' };
  if (!check || typeof check.passes !== 'boolean') return { ok: false, reason: 'no check result supplied — nothing to certify' };
  if (!check.passes) {
    return { ok: false, reason: 'the check did not pass', missing: check.missing ?? [] };
  }
  if (!Array.isArray(check.published_standard) || check.published_standard.length === 0) {
    return { ok: false, reason: 'the check names no standard — a certificate must carry what was required' };
  }
  return {
    ok: true,
    certificate: Object.freeze({
      schema: SCHEMA, kind, principal, level,
      artefact: artefact ?? null,
      /* THE EVIDENCE. Not a summary of it — the standard itself, so a reader can see what was
         required without asking anyone. */
      standard: Object.freeze(check.published_standard.map((s) => Object.freeze({ ...s }))),
      standard_name: standardName ?? null,
      requirements_met: check.published_standard.length,
      issued_on: on,
      /* How to disbelieve it. A certificate that does not tell you how to check it is asking for
         trust rather than offering proof. */
      reproduce: standardName ? `node slm-tutor/${standardName}` : null,
      status: 'valid',
    }),
  };
}

/**
 * Append to the ledger. Never replace, never remove.
 *
 * Returns a NEW ledger — the caller cannot mutate the old one by accident, and a caller that keeps
 * the old reference keeps a true historical view rather than a corrupted one.
 */
export const append = (ledger = [], entry) => Object.freeze([...ledger, Object.freeze(entry)]);

/**
 * Revoke by APPENDING a revocation, not by editing the issue.
 *
 * The original entry stays in the ledger, visible, and the revocation names its reason. A ledger
 * where a mistake can be made to have never happened is a ledger that cannot be used as evidence
 * for anything, including the things that are true.
 */
export function revoke(ledger, { certificateOf, reason, on }) {
  const issued = (ledger || []).filter((e) => e.event === 'issued' && e.certificate?.principal === certificateOf);
  if (issued.length === 0) return { ok: false, reason: 'no certificate issued to that principal' };
  if (!reason) return { ok: false, reason: 'a revocation must carry a reason — an unexplained one is indistinguishable from an error' };
  return { ok: true, ledger: append(ledger, { event: 'revoked', certificateOf, reason, on }) };
}

/**
 * The current standing of a principal's certificate: latest event wins, and a revocation is
 * visible rather than absent.
 */
export function standingOf(ledger = [], principal) {
  const mine = ledger.filter((e) => (e.certificate?.principal ?? e.certificateOf) === principal);
  if (mine.length === 0) return { held: false, status: 'none', history: [] };
  const last = mine[mine.length - 1];
  return {
    held: last.event === 'issued',
    status: last.event === 'issued' ? 'valid' : 'revoked',
    /* The whole history, always. A revoked certificate that vanished would let a reader believe
       nothing had ever been issued, which is a different and false statement. */
    history: mine.map((e) => ({ event: e.event, on: e.on, reason: e.reason ?? null })),
    certificate: last.event === 'issued' ? last.certificate : mine.filter((e) => e.event === 'issued').slice(-1)[0]?.certificate ?? null,
  };
}

export const WHY_NOT_AN_ASSERTION = 'An ordinary certificate asserts that someone completed something, which is a claim with no derivation — a reader can only believe the issuer. This one records a check: what was required, what was produced, when it passed, and how to run it again.';
export const WHY_APPEND_ONLY = 'Issuing appends and revoking appends. A revoked certificate stays in the record with its reason, because a ledger where a mistake can be made never to have happened cannot be evidence for anything — including the parts that are true.';
export const NOT_ISSUED_ON_READING = 'No certificate is issued for reading. A learner who has read every chapter and produced nothing has a record of reading, which is a different thing and is not called a certificate here.';
